import { IFieldsMapFactory } from "./shared/fields/map"
import { SharedFieldsService } from "./shared/fields/service"
import { IDatalayersMapFactory } from "./shared/layers/types"
import { IMapTopologyService } from "./shared/map/topo/types"
import { IModule } from "./shared/shell/types"
import { ExternalModuleContext } from "./shared/types"
import { OmniaModuleId } from "./shared/generated/enums"
import { IOmniaShell } from "./shared/shell"
import { IFieldsMapContainerOptions } from "./shared/fields/map"

export function setupExternalModuleHost(module: ng.IModule) {

    module.service("OmniaExternalModuleContext", createExternalModuleContext)
    module.directive("omniaExternalModule", createExternalModuleHost)

}

interface ExternalModule {

    /** Well-known module id */
    readonly moduleId: OmniaModuleId

    /** Readable module name */
    readonly moduleName: string

    /**
     * Map of dependencies to be provided to the "start" function below
     * The keys are used as identifiers by the module, and the values are
     * names registered with Angular's $injector service
     **/
    readonly dependencies: { [key: string]: string }

    /**
     * Called by Omnia to start the module
     * @param rootElement: the root HTML element in which the module is hosted
     * @param context: various predefined core services which
     * can be consumed by the module
     * @param dependencies: dependencies required by the
     * "dependencies" property
     */
    start(rootElement: HTMLElement, context: ExternalModuleContext, dependencies: { [key: string]: {} }): IModule

    /**
     * Called by Omnia to stop/cleanup the module (i.e., when another module is started)
     * @param rootElement: the root HTML element in which the module is hosted
     */
    cleanup(rootElement: HTMLElement): void

}

const inject = [
    "$injector",
    "$rootScope",
    "$location",
    "OmniaShell",
    "OmniaFieldsService",
    "OmniaFieldsMapFactory",
    "OmniaDatalayersMapFactory",
    "OmniaMapTopologyService"
]

function createExternalModuleContext(
    $injector: ng.auto.IInjectorService,
    $rootScope: ng.IRootScopeService,
    $location: ng.ILocationService,
    shell: IOmniaShell,
    fields: SharedFieldsService,
    fieldsMapFactory: IFieldsMapFactory,
    datalayersMapFactory: IDatalayersMapFactory,
    topo: IMapTopologyService
): ExternalModuleContext {

    const session = shell.getSession()
    const http = session.http

    return {

        http,
        session,
        fields,
        topo,

        apply() {
            $rootScope.$applyAsync()
        },

        createFieldsMap(options?: IFieldsMapContainerOptions) {
            return fieldsMapFactory.createMap(options)
        },

        createDataLayersMap(options?: IFieldsMapContainerOptions) {
            return datalayersMapFactory.createMap(options)
        },

        getRouteParams() {
            return shell.getRouteParams()
        },

        navigate(path: string) {
            $location.path(path)
            $rootScope.$applyAsync()
        },

        getService<T>(name: string) {
            return $injector.get<T>(name)
        },

        disableShell(disable: boolean) {
            return shell.disableShell(disable)
        },

        blockUi(block: string | boolean) {

            if (typeof block == "string") {
                shell.startBlockUI(block)
            }
            else if (block) {
                shell.startBlockUI()
            }
            else {
                shell.stopBlockUI()
            }

        }

    }

}

createExternalModuleContext.$inject = inject

function createExternalModuleHost() {

    const controller = ($scope: ng.IScope, $injector: ng.auto.IInjectorService, context: ExternalModuleContext, shell: IOmniaShell) => {

        function getModule(): ExternalModule {
            const args = $scope.$parent["$resolve"]
            const module = args["omniaModule"]
            return module
        }

        let rootElement: HTMLElement

        return {

            link(element: JQLite) {

                const module = getModule()
                rootElement = element[0]

                const dependencies = {}

                if (module.dependencies) {

                    for (const key in module.dependencies) {
                        const name = module.dependencies[key]
                        dependencies[key] = $injector.get(name)
                    }

                }

                const omniaModule = module.start(rootElement, context, dependencies)

                shell.setCurrentModule(omniaModule)

                omniaModule.refresh(true)

            },

            $onDestroy() {
                const module = getModule()
                if (rootElement) {
                    const e = rootElement
                    rootElement = undefined
                    module.cleanup(e)
                }
            }

        }

    }

    controller.$inject = ["$scope", "$injector", "OmniaExternalModuleContext", "OmniaShell"]

    const def = {

        scope: {},
        controller,
        controllerAs: "vm",
        bindToController: true,
        template: "<div></div>",

        link: (scope, element, attrs, ctrl: ReturnType<typeof controller>) => ctrl.link(element)

    }

    return def

}
